import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Table, Alert, Spinner } from 'react-bootstrap';
import { getOrderDetails, API_URL } from '../services/api';
import PrintableOrder from './PrintableOrder';

function OrderDetail() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchOrderDetails = async () => {
      if (!orderId) {
        setError('No order ID provided');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const data = await getOrderDetails(orderId);
        
        // Get customer name from session
        try {
          const sessionUser = JSON.parse(sessionStorage.getItem('user'));
          if (sessionUser?.customerName) {
            data.customerName = sessionUser.customerName;
          }
        } catch (error) {
          console.error('Error getting customer name from session:', error);
        }
        
        setOrder(data);
      } catch (error) {
        console.error('Error fetching order details:', error);
        setError(error.response?.data?.message || 'Failed to load order details. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchOrderDetails();
  }, [orderId]);

  const handleBack = () => {
    navigate('/orders');
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="text-center p-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <Container>
      <div className="mt-4 mb-4">
        <Button variant="secondary" onClick={handleBack} className="me-2">
          Back to Transactions
        </Button>
        <Button variant="primary" onClick={handlePrint}>
          Print Order
        </Button>
      </div>

      {error ? (
        <Alert variant="danger">{error}</Alert>
      ) : (
        <>
          <h2>Order #{order?.idTrans || 'N/A'}</h2>

          <Row className="mt-4">
            <Col md={6}>
              <Card className="mb-4 h-100">
                <Card.Header className="h-100-header">Shipping Information</Card.Header>
                <Card.Body className="h-100-body">
                  {order?.delName && <p><strong>Ship To:</strong> {order.delName}</p>}
                  {order?.delAddress && <p>{order.delAddress}</p>}
                  {(order?.delCity || order?.delState || order?.delZip) && (
                    <p>
                      {[
                        order.delCity,
                        order.delState,
                        order.delZip
                      ].filter(Boolean).join(', ')}
                    </p>
                  )}
                  {order?.delPhone && <p><strong>Phone:</strong> {order.delPhone}</p>}
                </Card.Body>
              </Card>
            </Col>

            <Col md={6}>
              <Card className="mb-4 h-100">
                <Card.Header className="h-100-header">Order Information</Card.Header>
                <Card.Body className="h-100-body">
                  <p><strong>Date:</strong> {order?.transDate ? new Date(order.transDate).toLocaleString() : ''}</p>
                  <p><strong>Reference:</strong> {order?.CustomerRef || ''}</p>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Card>
            <Card.Header>Order Items</Card.Header>
            <Card.Body>
              <div className="table-responsive">
                <Table striped bordered hover>
                  <thead>
                    <tr>
                      <th>Lot ID</th>
                      <th>Item Number</th>
                      <th>Description</th>
                      <th>Notes</th>
                      <th>PPU</th>
                      <th>Quantity</th>
                      <th>Total Pieces</th>
                    </tr>
                  </thead>
                  <tbody>
                    {order?.items?.map((item) => (
                      <tr key={item.idTrans2}>
                        <td>{item.recTrans || ''}</td>
                        <td>{item.itemNumber || ''}</td>
                        <td>{item.itemName || ''}</td>
                        <td>{item.notes || ''}</td>
                        <td>{Number(item.ppu || 1).toLocaleString()}</td>
                        <td>{Number(item.transQty || 0).toLocaleString()}</td>
                        <td>{Number((item.transQty || 0) * (item.ppu || 1)).toLocaleString()}</td>
                      </tr>
                    ))}
                    {(!order?.items || order.items.length === 0) && (
                      <tr>
                        <td colSpan="7" className="text-center">No items found</td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>

          <PrintableOrder order={order} />
        </>
      )}
    </Container>
  );
}

export default OrderDetail;
